import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
/**
 * Authorization Policy Settings Component
 *
 * Provides a link to the full Policy Configuration page
 * Only accessible to admin users
 */ export default function PolicyConfigSettings() {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-2xl font-bold text-foreground mb-2",
                        children: "Authorization Policies"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "Manage and test authorization policies powered by Cerbos. View loaded policies, test permission checks, and explore policy templates."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "bg-info-muted border border-info rounded-lg p-6",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-start space-x-4",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex-shrink-0",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "w-12 h-12 bg-info-muted rounded-lg flex items-center justify-center",
                                children: /*#__PURE__*/ _jsxs("svg", {
                                    className: "w-6 h-6 text-info",
                                    fill: "none",
                                    stroke: "currentColor",
                                    viewBox: "0 0 24 24",
                                    "aria-hidden": "true",
                                    children: [
                                        /*#__PURE__*/ _jsx("title", {
                                            children: "Security Shield"
                                        }),
                                        /*#__PURE__*/ _jsx("path", {
                                            strokeLinecap: "round",
                                            strokeLinejoin: "round",
                                            strokeWidth: 2,
                                            d: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                        })
                                    ]
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex-1",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-semibold text-foreground mb-2",
                                    children: "Policy Configuration Dashboard"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-foreground mb-4",
                                    children: "Access the full policy configuration interface to manage authorization rules, test permissions, and view detailed policy information."
                                }),
                                /*#__PURE__*/ _jsxs(Link, {
                                    to: "/policy-config",
                                    className: "inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors",
                                    children: [
                                        "Open Policy Configuration",
                                        /*#__PURE__*/ _jsxs("svg", {
                                            className: "ml-2 w-4 h-4",
                                            fill: "none",
                                            stroke: "currentColor",
                                            viewBox: "0 0 24 24",
                                            "aria-hidden": "true",
                                            children: [
                                                /*#__PURE__*/ _jsx("title", {
                                                    children: "Arrow Right"
                                                }),
                                                /*#__PURE__*/ _jsx("path", {
                                                    strokeLinecap: "round",
                                                    strokeLinejoin: "round",
                                                    strokeWidth: 2,
                                                    d: "M9 5l7 7-7 7"
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 md:grid-cols-2 gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "border rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("h4", {
                                className: "font-semibold text-foreground mb-2",
                                children: "Policy Management"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground mb-3",
                                children: "View and manage all loaded authorization policies from Cerbos"
                            }),
                            /*#__PURE__*/ _jsxs("ul", {
                                className: "text-sm text-muted-foreground space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("li", {
                                        children: "• List all active policies"
                                    }),
                                    /*#__PURE__*/ _jsx("li", {
                                        children: "• View policy details and rules"
                                    }),
                                    /*#__PURE__*/ _jsx("li", {
                                        children: "• Check policy versions"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "border rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("h4", {
                                className: "font-semibold text-foreground mb-2",
                                children: "Policy Testing"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground mb-3",
                                children: "Test authorization checks with custom principal and resource data"
                            }),
                            /*#__PURE__*/ _jsxs("ul", {
                                className: "text-sm text-muted-foreground space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("li", {
                                        children: "• Quick test policies"
                                    }),
                                    /*#__PURE__*/ _jsx("li", {
                                        children: "• Custom permission checks"
                                    }),
                                    /*#__PURE__*/ _jsx("li", {
                                        children: "• View detailed results"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "bg-warning-muted border border-warning rounded-lg p-4",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-start",
                    children: [
                        /*#__PURE__*/ _jsxs("svg", {
                            className: "w-5 h-5 text-warning mt-0.5 mr-3 flex-shrink-0",
                            fill: "currentColor",
                            viewBox: "0 0 20 20",
                            "aria-hidden": "true",
                            children: [
                                /*#__PURE__*/ _jsx("title", {
                                    children: "Warning"
                                }),
                                /*#__PURE__*/ _jsx("path", {
                                    fillRule: "evenodd",
                                    d: "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z",
                                    clipRule: "evenodd"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("h4", {
                                    className: "font-semibold text-warning mb-1",
                                    children: "Admin Only"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-warning",
                                    children: "Authorization policy management requires administrator privileges. Only internal users can view and test policies."
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
